// Unified customer timeline. The pure part (buildEvent) is testable; the writer
// performs a field-level push to customerBase/{key}/timeline (never overwrites).
import { ref, push, set } from 'firebase/database';
import { db } from '../../integrations/firebase';
import { customerKey } from './CustomerEventEngine';

// Canonical timeline event types (Phase 4) — every module uses these.
export const TIMELINE_EVENTS = {
    CUSTOMER_CREATED: 'customer_created',
    ORDER_CREATED: 'order_created',
    PAYMENT_RECEIVED: 'payment_received',
    COD_CONFIRMED: 'cod_confirmed',
    INVOICE_GENERATED: 'invoice_generated',
    COURIER_ASSIGNED: 'courier_assigned',
    AWB_GENERATED: 'awb_generated',
    PACKED: 'packed',
    SHIPPED: 'shipped',
    OUT_FOR_DELIVERY: 'out_for_delivery',
    DELIVERED: 'delivered',
    COMPLAINT_CREATED: 'complaint_ticket',
    DOCTOR_CASE: 'doctor_case',
    REFUND: 'refund',
    REPLACEMENT: 'replacement',
    RETURN_PICKUP: 'return_pickup',
    RESHIP: 'reship',
    SMS_SENT: 'complaint_sms_update',
    WHATSAPP_SENT: 'complaint_whatsapp_update',
    CALL_MADE: 'call_made',
    REMARK_ADDED: 'remark_added',
    INTERNAL_NOTE: 'internal_note',
    TASK_CREATED: 'task_created',
    FOLLOW_UP: 'follow_up',
    CALENDAR_REMINDER: 'calendar_reminder',
    OFFER_APPLIED: 'offer_applied',
    CAMPAIGN_SENT: 'campaign_sent',
    REFERRAL: 'referral',
    MANUAL_NOTE: 'manual_note',
    // Ekart courier lifecycle (Phase 4). Every event carries { awb, courier, status, updatedBy }.
    EKART_ASSIGNED: 'ekart_assigned',
    EKART_BOOKING_CREATED: 'ekart_booking_created',
    EKART_LABEL_GENERATED: 'ekart_label_generated',
    EKART_PICKUP_COMPLETED: 'ekart_pickup_completed',
    EKART_IN_TRANSIT: 'ekart_in_transit',
    EKART_OUT_FOR_DELIVERY: 'ekart_out_for_delivery',
    EKART_DELIVERY_ATTEMPT: 'ekart_delivery_attempt',
    EKART_DELIVERED: 'ekart_delivered',
    EKART_NDR: 'ekart_ndr',
    EKART_RTO: 'ekart_rto',
    EKART_CANCELLED: 'ekart_cancelled',
    EKART_REFUND: 'ekart_refund',
    EKART_RESHIP: 'ekart_reship',
    // Courier escalation lifecycle (Section 16)
    COURIER_ESCALATION_CREATED: 'courier_escalation_created',
    COURIER_ESCALATION_ASSIGNED: 'courier_escalation_assigned',
    COURIER_CONTACTED: 'courier_contacted',
    COURIER_RESPONSE_RECEIVED: 'courier_response_received',
    COURIER_FOLLOWUP: 'courier_followup',
    COURIER_WAITING_CUSTOMER: 'courier_waiting_customer',
    COURIER_WAITING_WAREHOUSE: 'courier_waiting_warehouse',
    COURIER_RESHIP: 'courier_reship',
    COURIER_REFUND: 'courier_refund',
    COURIER_RESOLVED: 'courier_resolved',
    COURIER_REOPENED: 'courier_reopened',
    COURIER_CLOSED: 'courier_closed',
};

const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null));

// Pure: build a normalized timeline event.
export const buildEvent = (type, payload = {}, createdBy = 'system') =>
    clean({ type, ...payload, createdBy, createdAt: payload.createdAt || Date.now() });

// Write one timeline event for a customer (by mobile). Field-level push.
export const recordTimelineEvent = async (mobile, type, payload = {}, createdBy = 'system') => {
    const key = customerKey(mobile);
    if (!key) return null;
    try {
        const eRef = push(ref(db, `customerBase/${key}/timeline`));
        await set(eRef, buildEvent(type, payload, createdBy));
        return eRef.key;
    } catch (e) {
        console.warn('[CustomerTimeline] write failed:', e.message);
        return null;
    }
};
